import { useEffect } from "react";

import * as THREE from "three";

const DURATION = 4500;

const START_DELAY = 900;

const PARTICLE_SIZE = 3.5;

const PARTICLE_GAP = 2;

const PARTICLE_OPACITY = 0.9;

const PARTICLE_DELAY_MAX = 0.1;

const MORPH_START = 0.8;

/*
 * Սա հենց այն correction-ն է,
 * որը particle text-ը բերում է DOM
 * text-ի իրական Y դիրքին։
 *
 * Եթե քո կոնկրետ font/rendering-ում
 * 1-2px տարբերություն մնա, փոխում ես
 * ՄԻԱՅՆ այս թիվը։
 */
const PARTICLE_TEXT_Y_OFFSET = 6;

// Hover

const HOVER_RADIUS = 30;

const HOVER_FORCE = 32;

const HOVER_RETURN_SPEED = 0.09;

const HOVER_MOVE_SPEED = 0.18;

const useParticleTextAnimation = ({
    heroRef,
    canvasRef,
    language,
}) => {
    useEffect(() => {
        const hero =
            heroRef.current;

        const canvas =
            canvasRef.current;

        if (
            !hero ||
            !canvas
        ) {
            return;
        }

        let animationFrame = null;

        let cleanupThree = null;

        let cancelled = false;

        const init = async () => {
            // ==========================================
            // WAIT FOR FONT
            // ==========================================

            if (
                document.fonts?.ready
            ) {
                await document.fonts.ready;
            }

            if (cancelled) {
                return;
            }

            const target =
                hero.querySelector(
                    '[data-particle-target="brand"]'
                );

            if (!target) {
                return;
            }

            const label =
                target.querySelector(
                    ".particle-text__label"
                );

            if (!label) {
                return;
            }

            // ==========================================
            // WAIT FOR FINAL LAYOUT
            // ==========================================

            await new Promise(
                (resolve) => {
                    requestAnimationFrame(
                        () => {
                            requestAnimationFrame(
                                resolve
                            );
                        }
                    );
                }
            );

            if (cancelled) {
                return;
            }

            // ==========================================
            // DOM POSITIONS
            // ==========================================

            const heroRect =
                hero.getBoundingClientRect();

            const targetRect =
                label.getBoundingClientRect();

            const width =
                heroRect.width;

            const height =
                heroRect.height;

            const targetX =
                targetRect.left -
                heroRect.left;

            const targetY =
                targetRect.top -
                heroRect.top;

            const targetWidth =
                targetRect.width;

            const targetHeight =
                targetRect.height;

            if (
                width <= 0 ||
                height <= 0 ||
                targetWidth <= 0 ||
                targetHeight <= 0
            ) {
                return;
            }

            // ==========================================
            // TEXT MASK
            // ==========================================

            const textCanvas =
                document.createElement(
                    "canvas"
                );

            /*
             * Այստեղ mask canvas-ի height-ը
             * հենց DOM label-ի height-ն է։
             *
             * Extra padding չկա։
             */
            textCanvas.width =
                Math.max(
                    1,
                    Math.ceil(
                        targetWidth
                    )
                );

            textCanvas.height =
                Math.max(
                    1,
                    Math.ceil(
                        targetHeight
                    )
                );

            const textCtx =
                textCanvas.getContext(
                    "2d"
                );

            if (!textCtx) {
                return;
            }

            const labelStyle =
                window.getComputedStyle(
                    label
                );

            const fontSize =
                parseFloat(
                    labelStyle.fontSize
                ) || 16;

            const parsedLineHeight =
                parseFloat(
                    labelStyle.lineHeight
                );

            const lineHeight =
                Number.isFinite(
                    parsedLineHeight
                )
                    ? parsedLineHeight
                    : targetHeight;

            textCtx.clearRect(
                0,
                0,
                textCanvas.width,
                textCanvas.height
            );

            textCtx.fillStyle =
                "#ffffff";

            textCtx.font =
                `${labelStyle.fontWeight} ` +
                `${labelStyle.fontSize} ` +
                `${labelStyle.fontFamily}`;

            textCtx.textAlign =
                "left";

            textCtx.textBaseline =
                "alphabetic";

            const text =
                String(
                    label.textContent ||
                    "Pay Ways"
                );

            const metrics =
                textCtx.measureText(
                    text
                );

            const ascent =
                metrics.actualBoundingBoxAscent ||
                fontSize * 0.8;

            const descent =
                metrics.actualBoundingBoxDescent ||
                fontSize * 0.2;

            const glyphHeight =
                ascent +
                descent;

            const leading =
                Math.max(
                    0,
                    lineHeight -
                    glyphHeight
                );

            /*
             * Mask-ի baseline։
             */
            const baselineY =
                leading / 2 +
                ascent;

            textCtx.fillText(
                text,
                0,
                baselineY
            );

            const imageData =
                textCtx.getImageData(
                    0,
                    0,
                    textCanvas.width,
                    textCanvas.height
                );

            // ==========================================
            // TARGET POSITIONS
            // ==========================================

            const targetPositionsArray =
                [];

            for (
                let y = 0;
                y <
                textCanvas.height;
                y += PARTICLE_GAP
            ) {
                for (
                    let x = 0;
                    x <
                    textCanvas.width;
                    x += PARTICLE_GAP
                ) {
                    const index =
                        (
                            y *
                                textCanvas.width +
                            x
                        ) * 4;

                    const alpha =
                        imageData.data[
                            index + 3
                        ];

                    if (
                        alpha <= 100
                    ) {
                        continue;
                    }

                    /*
                     * =================================================
                     * IMPORTANT FIX
                     * =================================================
                     *
                     * DOM label-ի top-ը targetY է։
                     *
                     * Canvas mask-ի pixel-ը y է։
                     *
                     * Particle տարբերակը մի փոքր վերև էր
                     * հայտնվում։
                     *
                     * Այդ պատճառով ամբողջ particle glyph-ը
                     * իջեցնում ենք PARTICLE_TEXT_Y_OFFSET-ով։
                     */
                    const finalY =
                        targetY +
                        y +
                        PARTICLE_TEXT_Y_OFFSET;

                    targetPositionsArray.push(
                        targetX + x,
                        -finalY,
                        0
                    );
                }
            }

            if (
                !targetPositionsArray.length
            ) {
                return;
            }

            const targetPositions =
                new Float32Array(
                    targetPositionsArray
                );

            const particleCount =
                targetPositions.length /
                3;

            // ==========================================
            // START POSITIONS
            // ==========================================

            const startPositions =
                new Float32Array(
                    targetPositions.length
                );

            for (
                let i = 0;
                i <
                startPositions.length;
                i += 3
            ) {
                startPositions[i] =
                    Math.random() *
                    width;

                startPositions[
                    i + 1
                ] =
                    -Math.random() *
                    height;

                startPositions[
                    i + 2
                ] = 0;
            }

            // ==========================================
            // PARTICLE DELAYS
            // ==========================================

            const particleDelays =
                new Float32Array(
                    particleCount
                );

            for (
                let i = 0;
                i <
                particleCount;
                i++
            ) {
                particleDelays[i] =
                    Math.random() *
                    PARTICLE_DELAY_MAX;
            }

            // ==========================================
            // THREE
            // ==========================================

            const scene =
                new THREE.Scene();

            const camera =
                new THREE.OrthographicCamera(
                    0,
                    width,
                    0,
                    -height,
                    0.1,
                    100
                );

            camera.position.z =
                10;

            const renderer =
                new THREE.WebGLRenderer({
                    canvas,
                    alpha: true,
                    antialias: true,
                });

            renderer.setPixelRatio(
                Math.min(
                    window.devicePixelRatio ||
                        1,
                    2
                )
            );

            renderer.setSize(
                width,
                height,
                false
            );

            // ==========================================
            // PARTICLE TEXTURE
            // ==========================================

            const particleCanvas =
                document.createElement(
                    "canvas"
                );

            particleCanvas.width =
                32;

            particleCanvas.height =
                32;

            const particleCtx =
                particleCanvas.getContext(
                    "2d"
                );

            if (!particleCtx) {
                renderer.dispose();

                return;
            }

            particleCtx.clearRect(
                0,
                0,
                32,
                32
            );

            particleCtx.beginPath();

            particleCtx.arc(
                16,
                16,
                14,
                0,
                Math.PI * 2
            );

            particleCtx.fillStyle =
                "#ffffff";

            particleCtx.fill();

            const particleTexture =
                new THREE.CanvasTexture(
                    particleCanvas
                );

            // ==========================================
            // GEOMETRY
            // ==========================================

            const geometry =
                new THREE.BufferGeometry();

            geometry.setAttribute(
                "position",
                new THREE.BufferAttribute(
                    startPositions.slice(),
                    3
                )
            );

            const material =
                new THREE.PointsMaterial({
                    color:
                        0xf0fffc,

                    size:
                        PARTICLE_SIZE,

                    sizeAttenuation:
                        false,

                    map:
                        particleTexture,

                    transparent:
                        true,

                    opacity:
                        0,

                    alphaTest:
                        0.1,

                    depthWrite:
                        false,
                });

            const points =
                new THREE.Points(
                    geometry,
                    material
                );

            scene.add(
                points
            );

            const positionAttribute =
                geometry.getAttribute(
                    "position"
                );

            // ==========================================
            // HOVER STATE
            // ==========================================

            const mouse = {
                x: 0,
                y: 0,

                targetX: 0,
                targetY: 0,

                active: false,
            };

            let hoverStrength =
                0;

            let introFinished =
                false;

            const particleRandom =
                new Float32Array(
                    particleCount *
                        2
                );

            for (
                let i = 0;
                i <
                particleCount;
                i++
            ) {
                particleRandom[
                    i * 2
                ] =
                    Math.random() *
                        2 -
                    1;

                particleRandom[
                    i * 2 + 1
                ] =
                    Math.random() *
                        2 -
                    1;
            }

            // ==========================================
            // MOUSE
            // ==========================================

            const handleMouseMove =
                (event) => {
                    if (
                        !introFinished
                    ) {
                        return;
                    }

                    const rect =
                        hero.getBoundingClientRect();

                    const x =
                        event.clientX -
                        rect.left;

                    const y =
                        event.clientY -
                        rect.top;

                    mouse.targetX =
                        x;

                    mouse.targetY =
                        -y;

                    /*
                     * Hover area-ում նույնպես օգտագործում ենք
                     * նույն corrected particle position-ը։
                     */
                    const correctedTargetY =
                        targetY +
                        PARTICLE_TEXT_Y_OFFSET;

                    const expandedLeft =
                        targetX -
                        HOVER_RADIUS;

                    const expandedRight =
                        targetX +
                        targetWidth +
                        HOVER_RADIUS;

                    const expandedTop =
                        correctedTargetY -
                        HOVER_RADIUS;

                    const expandedBottom =
                        correctedTargetY +
                        targetHeight +
                        HOVER_RADIUS;

                    mouse.active =
                        x >=
                            expandedLeft &&
                        x <=
                            expandedRight &&
                        y >=
                            expandedTop &&
                        y <=
                            expandedBottom;

                    if (
                        mouse.active
                    ) {
                        const localX =
                            x -
                            targetX;

                        /*
                         * DOM label mask-ը իրական label-ի
                         * coordinate-ով է աշխատում։
                         *
                         * Այստեղ offset չենք ավելացնում,
                         * որովհետև mask-ը DOM text-ի վրա է։
                         */
                        const localY =
                            y -
                            targetY;

                        const mask =
                            `radial-gradient(` +
                            `circle ${HOVER_RADIUS}px ` +
                            `at ${localX}px ${localY}px, ` +
                            `transparent 0%, ` +
                            `transparent 35%, ` +
                            `black 85%)`;

                        label.style.webkitMaskImage =
                            mask;

                        label.style.maskImage =
                            mask;
                    } else {
                        label.style.webkitMaskImage =
                            "none";

                        label.style.maskImage =
                            "none";
                    }
                };

            const handleMouseLeave =
                () => {
                    mouse.active =
                        false;

                    label.style.webkitMaskImage =
                        "none";

                    label.style.maskImage =
                        "none";
                };

            hero.addEventListener(
                "mousemove",
                handleMouseMove
            );

            hero.addEventListener(
                "mouseleave",
                handleMouseLeave
            );

            // ==========================================
            // HOVER ANIMATION
            // ==========================================

            const animateHover =
                () => {
                    if (
                        cancelled ||
                        !introFinished
                    ) {
                        return;
                    }

                    mouse.x +=
                        (
                            mouse.targetX -
                            mouse.x
                        ) *
                        HOVER_MOVE_SPEED;

                    mouse.y +=
                        (
                            mouse.targetY -
                            mouse.y
                        ) *
                        HOVER_MOVE_SPEED;

                    const targetStrength =
                        mouse.active
                            ? 1
                            : 0;

                    hoverStrength +=
                        (
                            targetStrength -
                            hoverStrength
                        ) *
                        0.12;

                    const currentPositions =
                        positionAttribute.array;

                    let hasMovement =
                        false;

                    for (
                        let i = 0;
                        i <
                        currentPositions.length;
                        i += 3
                    ) {
                        const particleIndex =
                            i / 3;

                        const baseX =
                            targetPositions[
                                i
                            ];

                        const baseY =
                            targetPositions[
                                i + 1
                            ];

                        const dx =
                            baseX -
                            mouse.x;

                        const dy =
                            baseY -
                            mouse.y;

                        const distance =
                            Math.sqrt(
                                dx *
                                    dx +
                                dy *
                                    dy
                            );

                        let desiredX =
                            baseX;

                        let desiredY =
                            baseY;

                        if (
                            mouse.active &&
                            distance <
                                HOVER_RADIUS
                        ) {
                            const safeDistance =
                                Math.max(
                                    distance,
                                    0.001
                                );

                            const normalizedX =
                                dx /
                                safeDistance;

                            const normalizedY =
                                dy /
                                safeDistance;

                            const influence =
                                1 -
                                distance /
                                    HOVER_RADIUS;

                            const easedInfluence =
                                influence *
                                influence;

                            const randomX =
                                particleRandom[
                                    particleIndex *
                                        2
                                ];

                            const randomY =
                                particleRandom[
                                    particleIndex *
                                        2 +
                                        1
                                ];

                            const force =
                                HOVER_FORCE *
                                easedInfluence *
                                hoverStrength;

                            desiredX +=
                                normalizedX *
                                force;

                            desiredY +=
                                normalizedY *
                                force;

                            desiredX +=
                                randomX *
                                force *
                                0.45;

                            desiredY +=
                                randomY *
                                force *
                                0.45;
                        }

                        const previousX =
                            currentPositions[
                                i
                            ];

                        const previousY =
                            currentPositions[
                                i + 1
                            ];

                        currentPositions[
                            i
                        ] +=
                            (
                                desiredX -
                                currentPositions[
                                    i
                                ]
                            ) *
                            HOVER_RETURN_SPEED;

                        currentPositions[
                            i + 1
                        ] +=
                            (
                                desiredY -
                                currentPositions[
                                    i + 1
                                ]
                            ) *
                            HOVER_RETURN_SPEED;

                        const movementX =
                            Math.abs(
                                previousX -
                                currentPositions[
                                    i
                                ]
                            );

                        const movementY =
                            Math.abs(
                                previousY -
                                currentPositions[
                                    i +
                                        1
                                ]
                            );

                        if (
                            movementX >
                                0.01 ||
                            movementY >
                                0.01
                        ) {
                            hasMovement =
                                true;
                        }
                    }

                    positionAttribute.needsUpdate =
                        true;

                    if (
                        mouse.active ||
                        hoverStrength >
                            0.01 ||
                        hasMovement
                    ) {
                        material.opacity =
                            PARTICLE_OPACITY *
                            Math.max(
                                hoverStrength,
                                0.15
                            );
                    } else {
                        material.opacity =
                            0;
                    }

                    renderer.render(
                        scene,
                        camera
                    );

                    animationFrame =
                        requestAnimationFrame(
                            animateHover
                        );
                };

            // ==========================================
            // INTRO
            // ==========================================

            let startTime =
                null;

            let hideTimer =
                null;

            const animateIntro =
                (time) => {
                    if (
                        cancelled
                    ) {
                        return;
                    }

                    if (
                        startTime ===
                        null
                    ) {
                        startTime =
                            time;
                    }

                    const elapsed =
                        time -
                        startTime;

                    const progress =
                        Math.min(
                            elapsed /
                                DURATION,
                            1
                        );

                    const currentPositions =
                        positionAttribute.array;

                    for (
                        let i = 0;
                        i <
                        currentPositions.length;
                        i += 3
                    ) {
                        const particleIndex =
                            i / 3;

                        const delay =
                            particleDelays[
                                particleIndex
                            ];

                        let particleProgress =
                            (
                                progress -
                                delay
                            ) /
                            (
                                1 -
                                delay
                            );

                        particleProgress =
                            Math.max(
                                0,
                                Math.min(
                                    1,
                                    particleProgress
                                )
                            );

                        const eased =
                            1 -
                            Math.pow(
                                1 -
                                    particleProgress,
                                4
                            );

                        currentPositions[
                            i
                        ] =
                            startPositions[
                                i
                            ] +
                            (
                                targetPositions[
                                    i
                                ] -
                                startPositions[
                                    i
                                ]
                            ) *
                            eased;

                        currentPositions[
                            i + 1
                        ] =
                            startPositions[
                                i + 1
                            ] +
                            (
                                targetPositions[
                                    i + 1
                                ] -
                                startPositions[
                                    i + 1
                                ]
                            ) *
                            eased;
                    }

                    positionAttribute.needsUpdate =
                        true;

                    // ==================================
                    // CROSSFADE
                    // ==================================

                    if (
                        progress >=
                        MORPH_START
                    ) {
                        let morphProgress =
                            (
                                progress -
                                MORPH_START
                            ) /
                            (
                                1 -
                                MORPH_START
                            );

                        morphProgress =
                            Math.max(
                                0,
                                Math.min(
                                    1,
                                    morphProgress
                                )
                            );

                        const smoothMorph =
                            morphProgress *
                            morphProgress *
                            (
                                3 -
                                2 *
                                    morphProgress
                            );

                        label.style.opacity =
                            String(
                                smoothMorph
                            );

                        material.opacity =
                            PARTICLE_OPACITY *
                            (
                                1 -
                                smoothMorph
                            );
                    } else {
                        label.style.opacity =
                            "0";

                        material.opacity =
                            PARTICLE_OPACITY;
                    }

                    renderer.render(
                        scene,
                        camera
                    );

                    if (
                        progress < 1
                    ) {
                        animationFrame =
                            requestAnimationFrame(
                                animateIntro
                            );

                        return;
                    }

                    // ==================================
                    // INTRO FINISHED
                    // ==================================

                    label.style.transition =
                        "none";

                    label.style.opacity =
                        "1";

                    material.opacity =
                        0;

                    /*
                     * Exact particle target positions.
                     */
                    for (
                        let i = 0;
                        i <
                        currentPositions.length;
                        i += 3
                    ) {
                        currentPositions[
                            i
                        ] =
                            targetPositions[
                                i
                            ];

                        currentPositions[
                            i + 1
                        ] =
                            targetPositions[
                                i + 1
                            ];

                        currentPositions[
                            i + 2
                        ] = 0;
                    }

                    positionAttribute.needsUpdate =
                        true;

                    renderer.render(
                        scene,
                        camera
                    );

                    introFinished =
                        true;

                    animationFrame =
                        requestAnimationFrame(
                            animateHover
                        );
                };

            // ==========================================
            // INITIAL
            // ==========================================

            label.style.transition =
                "none";

            label.style.opacity =
                "1";

            material.opacity =
                0;

            renderer.render(
                scene,
                camera
            );

            // ==========================================
            // START INTRO
            // ==========================================

            hideTimer =
                setTimeout(
                    () => {
                        if (
                            cancelled
                        ) {
                            return;
                        }

                        label.style.transition =
                            "opacity 120ms ease-out";

                        label.style.opacity =
                            "0";

                        material.opacity =
                            PARTICLE_OPACITY;

                        startTime =
                            null;

                        animationFrame =
                            requestAnimationFrame(
                                animateIntro
                            );
                    },
                    START_DELAY
                );

            // ==========================================
            // CLEANUP
            // ==========================================

            cleanupThree =
                () => {
                    clearTimeout(
                        hideTimer
                    );

                    if (
                        animationFrame
                    ) {
                        cancelAnimationFrame(
                            animationFrame
                        );
                    }

                    hero.removeEventListener(
                        "mousemove",
                        handleMouseMove
                    );

                    hero.removeEventListener(
                        "mouseleave",
                        handleMouseLeave
                    );

                    geometry.dispose();

                    material.dispose();

                    particleTexture.dispose();

                    renderer.dispose();

                    label.style.transition =
                        "";

                    label.style.opacity =
                        "";

                    label.style.webkitMaskImage =
                        "";

                    label.style.maskImage =
                        "";
                };
        };

        init();

        return () => {
            cancelled = true;

            if (
                animationFrame
            ) {
                cancelAnimationFrame(
                    animationFrame
                );
            }

            cleanupThree?.();
        };
    }, [
        heroRef,
        canvasRef,
        language,
    ]);
};

export default useParticleTextAnimation;